import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Globe, Loader2, Sparkles, ArrowRight, ArrowLeft, Check } from "lucide-react";
import { Card, PageHeader, Field, inputCls, Btn, CheckboxGroup } from "../../components/dash/Kit";
import VerifyNotice from "../../components/dash/VerifyNotice";
import { INDUSTRY_OPTIONS } from "../../data/site";
import { api, errMessage } from "../../lib/api";

const GOALS = ["More customers", "More reservations", "More appointments", "Brand awareness",
  "Repeat customers", "Promote offers", "Hire staff", "Grow followers"];
const PERSONALITY = ["Warm", "Modern", "Informal", "Professional", "Playful", "Premium",
  "Local", "Bold", "Calm"];
const PLATFORMS = ["Instagram", "Facebook", "LinkedIn", "TikTok"];
const LANGUAGES = ["English", "Dutch", "Dutch + English"];

const STEPS = ["Website", "Industry", "Socials", "Goal", "Personality", "Photos", "Review"];

export default function QuickSetup() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    website: "", industry: "", business_name: "", description: "", location: "",
    services: "", opening_hours: "", goals: [], personality: [],
    content_language: "English", platforms: ["Instagram"],
  });
  const [imported, setImported] = useState(null);
  const [importing, setImporting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [suggestion, setSuggestion] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setVal = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const importBusiness = async () => {
    if (!form.website.trim()) {
      toast.error("Enter your website address first.");
      return;
    }
    setImporting(true);
    try {
      const { data } = await api.post("/engine/import-website", { url: form.website });
      setImported(data);
      setForm((f) => ({
        ...f,
        business_name: data.business_name || f.business_name,
        description: data.description || f.description,
        industry: data.industry || f.industry,
        location: data.location || f.location,
        services: Array.isArray(data.services) ? data.services.join(", ") : data.services || f.services,
        opening_hours: data.opening_hours || f.opening_hours,
      }));
      toast.success("We found this information about your business. Please check that everything is correct.");
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setImporting(false);
    }
  };

  const finish = async () => {
    if (!form.business_name.trim()) {
      toast.error("Please tell us your business name.");
      setStep(1);
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/engine/quick-setup", form);
      setSuggestion(data.suggestion);
      setStep(6);
      toast.success("Your Brand Profile is ready.");
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="quick-setup-page">
      <PageHeader
        testId="quick-setup-header"
        title="Let's put your social media on autopilot."
        body="Seven short steps. PostMint fills in as much as it can — you only correct what's wrong."
      />

      <ol className="mb-6 flex flex-wrap gap-2" data-testid="quick-setup-steps">
        {STEPS.map((s, i) => (
          <li key={s}>
            <button
              type="button"
              onClick={() => setStep(i)}
              data-testid={`quick-step-${s.toLowerCase()}`}
              className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                i === step
                  ? "border-slate-900 bg-slate-900 text-white"
                  : i < step
                    ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                    : "border-slate-200 bg-white text-slate-500"
              }`}
            >
              {i < step && <Check className="h-3 w-3" aria-hidden="true" />}
              {i + 1}. {s}
            </button>
          </li>
        ))}
      </ol>

      {step === 0 && (
        <Card testId="quick-step-website-card">
          <p className="font-display text-xl font-semibold text-slate-900">What's your website?</p>
          <p className="mt-2 text-sm text-slate-600">
            PostMint reads the public information on your site and suggests your profile. You review everything after.
          </p>
          <div className="mt-5 flex flex-col gap-3 sm:flex-row">
            <input
              value={form.website}
              onChange={set("website")}
              placeholder="https://example.nl"
              data-testid="quick-website-input"
              className={inputCls}
            />
            <Btn onClick={importBusiness} disabled={importing} testId="quick-import-button" className="shrink-0">
              {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Globe className="h-4 w-4" />}
              {importing ? "Reading your site…" : "Import My Business"}
            </Btn>
          </div>

          {imported && (
            <div className="mt-6 space-y-4" data-testid="quick-import-result">
              <p className="text-sm font-semibold text-slate-900">
                We found this information about your business. Please check that everything is correct.
              </p>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Business name">
                  <input value={form.business_name} onChange={set("business_name")} className={inputCls} data-testid="quick-name-input" />
                </Field>
                <Field label="Industry">
                  <input value={form.industry} onChange={set("industry")} className={inputCls} data-testid="quick-industry-input" />
                </Field>
                <Field label="Location">
                  <input value={form.location} onChange={set("location")} className={inputCls} />
                </Field>
                <Field label="Opening hours" hint="Only what your website actually states.">
                  <input value={form.opening_hours} onChange={set("opening_hours")} className={inputCls} />
                </Field>
              </div>
              <Field label="What your business does">
                <textarea value={form.description} onChange={set("description")} rows={3} className={inputCls} />
              </Field>
              <Field label="Products & services">
                <textarea value={form.services} onChange={set("services")} rows={2} className={inputCls} />
              </Field>
              {imported.brand_colors?.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-slate-800">Brand colours we spotted</p>
                  <div className="mt-2 flex gap-2">
                    {imported.brand_colors.map((c) => (
                      <span key={c} className="flex items-center gap-2 rounded-xl border border-slate-200 px-2.5 py-1.5 text-xs">
                        <span className="h-4 w-4 rounded" style={{ background: c }} aria-hidden="true" />
                        {c}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {Object.keys(imported.socials || {}).length > 0 && (
                <div data-testid="quick-import-socials">
                  <p className="text-sm font-semibold text-slate-800">Social accounts we found</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {Object.entries(imported.socials).map(([name, link]) => (
                      <a
                        key={name}
                        href={link}
                        target="_blank"
                        rel="noreferrer"
                        className="rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold capitalize text-slate-700 hover:border-slate-900"
                      >
                        {name}
                      </a>
                    ))}
                  </div>
                </div>
              )}
              {imported.logo_url && (
                <img
                  src={imported.logo_url}
                  alt="Logo found on your website"
                  data-testid="quick-import-logo"
                  className="h-14 w-auto max-w-[200px] rounded-lg border border-slate-200 bg-white object-contain p-1"
                />
              )}
              <VerifyNotice items={imported.uncertain} testId="quick-import-verify" />
            </div>
          )}

          <div className="mt-6 flex gap-3">
            <Btn variant="dark" onClick={() => setStep(1)} testId="quick-next-0">
              Continue <ArrowRight className="h-4 w-4" />
            </Btn>
            <Btn variant="ghost" onClick={() => setStep(1)} testId="quick-skip-import">
              I'll fill it in myself
            </Btn>
          </div>
        </Card>
      )}

      {step === 1 && (
        <Card testId="quick-step-industry-card">
          <p className="font-display text-xl font-semibold text-slate-900">What kind of business is it?</p>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <Field label="Business name">
              <input value={form.business_name} onChange={set("business_name")} className={inputCls} data-testid="quick-business-name" />
            </Field>
            <Field label="Industry">
              <select value={form.industry} onChange={set("industry")} className={inputCls} data-testid="quick-industry-select">
                <option value="">Choose your industry</option>
                {INDUSTRY_OPTIONS.map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </Field>
          </div>
          <div className="mt-4">
            <Field label="In one or two lines, what do you offer?">
              <textarea value={form.description} onChange={set("description")} rows={3} className={inputCls} data-testid="quick-description" />
            </Field>
          </div>
          <NavRow onBack={() => setStep(0)} onNext={() => setStep(2)} index={1} />
        </Card>
      )}

      {step === 2 && (
        <Card testId="quick-step-socials-card">
          <p className="font-display text-xl font-semibold text-slate-900">Where do you want to post?</p>
          <p className="mt-2 text-sm text-slate-600">
            Pick the platforms you care about. You can connect the accounts themselves in Connections.
          </p>
          <div className="mt-5">
            <CheckboxGroup name="quick-platforms" options={PLATFORMS} value={form.platforms} onChange={(v) => setVal("platforms", v)} />
          </div>
          <div className="mt-5">
            <Field label="Content language">
              <select value={form.content_language} onChange={set("content_language")} className={inputCls} data-testid="quick-language">
                {LANGUAGES.map((l) => (
                  <option key={l} value={l}>{l}</option>
                ))}
              </select>
            </Field>
          </div>
          <NavRow onBack={() => setStep(1)} onNext={() => setStep(3)} index={2} />
        </Card>
      )}

      {step === 3 && (
        <Card testId="quick-step-goal-card">
          <p className="font-display text-xl font-semibold text-slate-900">What should social media do for you?</p>
          <div className="mt-5">
            <CheckboxGroup name="quick-goals" options={GOALS} value={form.goals} onChange={(v) => setVal("goals", v)} />
          </div>
          <NavRow onBack={() => setStep(2)} onNext={() => setStep(4)} index={3} />
        </Card>
      )}

      {step === 4 && (
        <Card testId="quick-step-personality-card">
          <p className="font-display text-xl font-semibold text-slate-900">How should you sound?</p>
          <div className="mt-5">
            <CheckboxGroup name="quick-personality" options={PERSONALITY} value={form.personality} onChange={(v) => setVal("personality", v)} columns={3} />
          </div>
          <NavRow onBack={() => setStep(3)} onNext={() => setStep(5)} index={4} />
        </Card>
      )}

      {step === 5 && (
        <Card testId="quick-step-photos-card">
          <p className="font-display text-xl font-semibold text-slate-900">Add your logo and photos</p>
          <p className="mt-2 text-sm text-slate-600">
            Your own photos make content look like your business. You can do this now or later — it won't block you.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Btn variant="outline" onClick={() => navigate("/dashboard/media")} testId="quick-open-media">
              Open my media library
            </Btn>
          </div>
          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <Btn variant="outline" onClick={() => setStep(4)} testId="quick-back-5">
              <ArrowLeft className="h-4 w-4" /> Back
            </Btn>
            <Btn onClick={finish} disabled={saving} testId="quick-build-profile-button">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              {saving ? "Building your profile…" : "Build my Brand Profile"}
            </Btn>
          </div>
        </Card>
      )}

      {step === 6 && (
        <div className="space-y-5" data-testid="quick-review-card">
          <Card>
            <p className="font-display text-xl font-semibold text-slate-900">Here's your Brand Profile</p>
            <p className="mt-2 text-sm text-slate-600">Everything here is editable — nothing is set in stone.</p>
            <dl className="mt-5 grid gap-4 sm:grid-cols-2">
              {[
                ["Brand", form.business_name],
                ["Industry", form.industry],
                ["Audience", suggestion?.audience],
                ["Tone", suggestion?.tone],
                ["Main goals", (suggestion?.goals || form.goals).join?.(", ")],
                ["Language", form.content_language],
              ].map(([label, value]) => (
                <div key={label}>
                  <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</dt>
                  <dd className="mt-1 text-sm text-slate-800">{value || "—"}</dd>
                </div>
              ))}
            </dl>
            {suggestion?.pillars?.length > 0 && (
              <div className="mt-5">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Content pillars</p>
                <div className="mt-2 flex flex-wrap gap-2" data-testid="quick-review-pillars">
                  {suggestion.pillars.map((p) => (
                    <span key={p.name} className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700">
                      {p.name}
                    </span>
                  ))}
                </div>
              </div>
            )}
            <VerifyNotice items={suggestion?.verify} testId="quick-review-verify" />
            <div className="mt-6 flex flex-wrap gap-3">
              <Btn onClick={() => navigate("/dashboard/create")} testId="quick-create-first-post">
                Create my first posts <ArrowRight className="h-4 w-4" />
              </Btn>
              <Btn variant="outline" onClick={() => navigate("/dashboard/brand")} testId="quick-open-brand">
                Edit my brand
              </Btn>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

const NavRow = ({ onBack, onNext, index }) => (
  <div className="mt-6 flex gap-3">
    <Btn variant="outline" onClick={onBack} testId={`quick-back-${index}`}>
      <ArrowLeft className="h-4 w-4" /> Back
    </Btn>
    <Btn variant="dark" onClick={onNext} testId={`quick-next-${index}`}>
      Continue <ArrowRight className="h-4 w-4" />
    </Btn>
  </div>
);
